// GroupsList.tsx
import React, { useMemo, useState } from 'react';
import { Box, Button, Card, CardContent, Checkbox, Divider, FormControlLabel, IconButton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { DataGrid, GridColDef, type GridRenderCellParams, } from '@mui/x-data-grid';
import { api } from "../../../api/groupApi";
import { api as plateApi } from "../../../api/plateApi";
import { useFetch } from "../../../hooks/useFetch";
import { Group } from '../../../types/group';
import { usePresetDialogs } from '../../dialog';
import { EntityPath } from '../../../types/plate';
import ActionButtons from '../../ActionButtons';
import { FormModal } from '../../modal/FormModal';
import GroupForm from './GroupForm';
import { useTranslation } from 'react-i18next';
import { Delete, Edit } from '@mui/icons-material';
import LocalizedGrid, { CustomGridColDef } from '../../localizedGrid/LocalizedGridCook3h';
import i18n from '../../../i18n';
import { useAuthStore } from '../../../store/useAuthStore3';
import { Subtenant, subtenantApi } from '../../../api/subtenantApi';

export const GroupsList: React.FC = () => {
    const { t } = useTranslation();
    const { data, setData, error } = useFetch<Group[]>(api.get, []);
    const { data: entityPaths, } = useFetch<EntityPath[]>(plateApi.getEntityPaths, []);
    const { data: subtenants } = useFetch<Subtenant[]>(subtenantApi.get, []);
    const { customerId, subtenantId } = useAuthStore();
    const { showConfirm, DialogComponent } = usePresetDialogs();
    const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

// onlyOwn section
    const [onlyOwn, setOnlyOwn] = useState(!subtenantId);

    const handleOwnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setOnlyOwn(event.target.checked);
    };

    const filteredData = useMemo(() => {
        return !onlyOwn ? data : data.filter(row => !row.subtenant_id);
    }, [data, onlyOwn]);
//
    
    if (!customerId) {
        return <Typography>t('customer.loading')</Typography>;
    }    

    const columns: CustomGridColDef[] = [
        { field: "group_id", headerName: "ID", type: "number", flex: 0.5, width: 50, },
        { field: "name", headerName: t('Group.name'), type: "string", flex: 2, width: 200 },
        { field: "is_white", headerName: t('Group.is_white'), type: "boolean", flex: 1.5, width: 100},       
        { field: "subtenant_id", headerName: t('PlatesList.subtenant'), type: "singleSelect", flex: 2, width: 100, display: 'flex',
            valueOptions: subtenants.map( subtenant => ({value: subtenant.subtenant_id, label: subtenant.name})),
            hidden: onlyOwn || !!subtenantId,
            renderCell: (params: GridRenderCellParams<Subtenant>) => {
                return (
                    <Typography>
                        {subtenants.find(s => s.subtenant_id === params.row.subtenant_id)?.name}
                    </Typography>
                );
            }
        },
        {
            field: 'actions',
            headerName: t('coomon.actions'),
            width: 200,
            sortable: false,
            renderCell: (params: GridRenderCellParams<Group>) => {
                return (
                    <ActionButtons actions={[
                        {type: 'edit', action: () => handleOpenModal(params.row), },
                        {type: 'delete', action: () => handleDelete(params.row.group_id), },
                    ]} />
                );
            },
        },
    ];

    const handleUpdate = async (data: Group) => {
        if (!!selectedGroup) {
            await api.put(data);
            await api.putGroupToEntity(customerId, data.group_id, data.entity_ids);
            setData((prev) => prev.map(group => group.group_id === data.group_id ? data : group));
        } else {
            const newData = await api.post(data);
            setData((prev) => [...prev, newData]);
        }
        handleCloseModal();
    };

    const handleDelete = async (group_id: number) => {
        const result = await showConfirm(
            // 'Ви дійсно хочете видалити групу: ' + group_id + '?', 
            // 'Підтвердження видалення');
            t('common.delete_question', {id: group_id}),
            t('common.delete_confirmation'));
        if (!result) return;

        await api.delete(group_id);

        const updatedPoints = data.filter(group => group_id !== group.group_id);
        setData(updatedPoints);
        console.log(`Group with ID ${group_id} deleted.`);
    };
    
    const handleOpenModal = (group: Group | null) => {
        setSelectedGroup(group);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => setIsModalOpen(false);
  
    // --- Рендер карточки для мобильных устройств ---
    const renderMobileCard = (group: Group) => (
        <Card key={group.group_id} sx={{ mb: 2, boxShadow: 2 }}>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" color="primary">
                        {group.name}
                    </Typography>
                    <Box>
                        <IconButton onClick={() => handleOpenModal(group)} size="small"><Edit /></IconButton>
                        <IconButton onClick={() => handleDelete(group.group_id)} size="small" color="error"><Delete /></IconButton>
                    </Box>
                </Box>
                
                <Divider sx={{ my: 1 }} />
                
                {/* <Stack spacing={0.5}>
                    <Typography variant="body2"><strong>{t('PlatesList.group')}:</strong> {groups.find(g => g.group_id === group.group_id)?.name}</Typography>
                    <Typography variant="body2"><strong>{t('PlatesList.car_owner')}:</strong> {group.car_owner}</Typography>
                    <Typography variant="body2"><strong>{t('PlatesList.car_brand')}:</strong> {group.car_brand}</Typography>
                    <Box display="flex" alignItems="center">
                        <Typography variant="body2"><strong>OTP:</strong></Typography>
                        <Checkbox checked={group.is_otp} disabled size="small" />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        {group.valid_since} — {group.valid_to}
                    </Typography>
                </Stack> */}
            </CardContent>
        </Card>
    );

    return (
  
        <Box sx={{ width: '100%', p: isMobile ? 1 : 0 }}>
        {/* <Box sx={{ height: 400, width: '100%' }}> */}
            
            {DialogComponent}
            
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Button 
                    variant="contained" 
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenModal(null)}
                >
                    {/* Add New Group */}
                    {t('GroupsList.Add_New_Group')}
                </Button>

                {!subtenantId && (
                <FormControlLabel
                    control={
                        <Checkbox 
                            checked={onlyOwn} 
                            onChange={handleOwnChange} 
                            color="primary"
                        />
                    }
                    label={t('GroupList.Only_Own')}
                    sx={{ 
                        whiteSpace: 'nowrap', // Чтобы текст не переносился на новую строку
                        marginLeft: isMobile ? 0 : 1 // Небольшой отступ слева на десктопе
                    }}
                />
                )}

            </Stack>
            {/* Основной контент: Сетка или Карточки */}
            {isMobile ? (
                <Box sx={{ pb: 2 }}>
                    {data.map(plate => renderMobileCard(plate))}
                </Box>
            ) : (
            <LocalizedGrid
                lang={i18n.language as any}
                gridId='GroupsList'
                rows={filteredData}
                columns={columns} 
                getRowId={(row) => row.group_id}
                sx={{ maxWidth: '800px' }}
                hideToolbar={true}
            />
            )}

            <Typography color='error'>{error?.message}</Typography>

            <FormModal open={isModalOpen} onClose={handleCloseModal}>
                <GroupForm
                    group={selectedGroup}
                    entityPaths={entityPaths}
                    onSave={handleUpdate}
                    onCancel={handleCloseModal}
                />
            </FormModal>
        </Box>
  
    );
};